import React, { useEffect, useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Axios from "axios";
import { safeJwtDecode } from "../../utilities/tokenUtils";
import DOMPurify from 'dompurify';
import UserContext from "../../context/UserContext";
import toast from 'react-hot-toast';

const schema = yup.object().shape({
    displayName: yup.string()
        .required('Nombre es requerido')
        .min(2, 'Mínimo 2 caracteres')
        .max(50, 'Máximo 50 caracteres')
        .matches(/^[a-zA-Z0-9áéíóúñü\s]+$/, 'Solo letras, números y espacios'),
    password: yup.string().when('$showPasswordFields', {
        is: true,
        then: schema => schema
            .required('Contraseña requerida')
            .min(14, 'Mínimo 14 caracteres')
            .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Debe contener mayúscula, minúscula y número')
            .matches(/[!@#$%^&*(),.?":{}|<>\-_+=[\]\\/]/, 'Debe incluir al menos un carácter especial'),
        otherwise: schema => schema.notRequired()
    }),
    passwordCheck: yup.string().when('$showPasswordFields', {
        is: true,
        then: schema => schema
            .oneOf([yup.ref('password'), null], 'Las contraseñas deben coincidir')
            .required('Confirma la contraseña'),
        otherwise: schema => schema.notRequired()
    }),
    role: yup.string().required('Rol es requerido'),
    status: yup.boolean()
});

export default function UserUpdate() {
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResettingMfa, setIsResettingMfa] = useState(false);
    const { register, handleSubmit, resetField, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema),
        context: { showPasswordFields }
    });
    const { userData } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const id = params.get('id');

    const decoded = safeJwtDecode(userData.token);
    const userRole = decoded.role ?? null;
    const userDisplayName = decoded.displayName ?? null;
    const loggedUserId = decoded.id ?? null;
    const canResetMfa = userRole === 'administrador' && id && id !== loggedUserId;

    useEffect(() => {
        if (!userData.token) {
            navigate('/admin/login');
        } else {
            if (decoded.role !== 'administrador') navigate('/');
        }

        async function fetchData() {
            try {
                const userRes = await Axios.get(
                    `${process.env.REACT_APP_BACK_DOMAIN}/users/findone/${id}`,
                    { headers: { "x-auth-token": userData.token } }
                );
                reset({
                    displayName: userRes.data.displayName,
                    role: userRes.data.role,
                    status: userRes.data.status
                });
            } catch (err) {
                console.error("Error al cargar usuario:", err);
            }
        }
        fetchData();
    }, [navigate, id, userData.token, reset, decoded.role]);



    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            if (userData.token) {
                const sanitizedData = {
                    id,
                    displayName: DOMPurify.sanitize(data.displayName),
                    password: data.password ? DOMPurify.sanitize(data.password) : undefined,
                    passwordCheck: data.passwordCheck ? DOMPurify.sanitize(data.passwordCheck) : undefined,
                    role: data.role,
                    status: data.status,
                    modificator: userDisplayName
                };

                await Axios.put(`${process.env.REACT_APP_BACK_DOMAIN}/users/update`, sanitizedData, {
                    headers: {
                        "x-auth-token": userData.token
                    }
                });
                toast.success("Usuario actualizado correctamente");
                navigate('/admin/users');
            }
        } catch (err) {
            toast.error(err.response?.data?.msg || "Error al actualizar usuario");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResetMfa = async () => {
        if (!window.confirm('¿Resetear el MFA de este usuario? Deberá reconfigurarlo en su próximo inicio de sesión.')) return;
        setIsResettingMfa(true);
        try {
            const res = await Axios.put(
                `${process.env.REACT_APP_BACK_DOMAIN}/users/reset-mfa/${id}`,
                {},
                { headers: { "x-auth-token": userData.token } }
            );
            toast.success(res.data.msg || 'MFA reseteado correctamente');
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Error al resetear MFA');
        } finally {
            setIsResettingMfa(false);
        }
    };

    return (
        <div className="page">
            <button className="Button Button_primary" onClick={() => navigate('/admin/users')}>Regresar</button>
            <div className="container">
                <h2>Actualizar Usuario</h2>
                <form className="form" onSubmit={handleSubmit(onSubmit)}>
                    <div className="Form-Field">
                        <label className="Form-Label" htmlFor="displayName">Nombre de Usuario: </label>
                        <input
                            className="Form-Input"
                            id="displayName"
                            {...register('displayName')}
                        />
                        {errors.displayName && <span>{errors.displayName.message}</span>}
                    </div>
                    {userRole === 'administrador' && (
                        <>
                            <div className="Form-Field">
                                <label className="Form-Label" htmlFor="role">Rol: </label>
                                <select className="Form-Input" id="role" {...register('role')}>
                                    <option value="administrador">Administrador</option>
                                    <option value="redactor">Redactor</option>
                                </select>
                                {errors.role && <span>{errors.role.message}</span>}
                            </div>
                            <div className="Form-Field">
                                <label className="Form-Label" htmlFor="status">Estado: </label>
                                <div className="switch">
                                    <label>
                                        <input
                                            type="checkbox"
                                            id="status"
                                            {...register('status')}
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>
                                {errors.status && <span>{errors.status.message}</span>}
                            </div>
                        </>
                    )}
                    <button
                        type="button"
                        className="Button"
                        style={{ marginRight: '1rem', marginBottom: '1rem' }}
                        onClick={() => {
                            setShowPasswordFields(!showPasswordFields);
                            if (!showPasswordFields) {
                                resetField('password');
                                resetField('passwordCheck');
                            }
                        }}>
                        {showPasswordFields ? "Cancelar" : "Cambiar Contraseña"}
                    </button>
                    <div className={!showPasswordFields ? "hidden" : ""}>
                        <div className="Form-Field">
                            <label className="Form-Label" htmlFor="password">Contraseña:</label>
                            <input
                                className="Form-Input"
                                type="password"
                                id="password"
                                {...register('password')}
                            />
                            {errors.password && <span>{errors.password.message}</span>}
                        </div>
                        <div className="Form-Field">
                            <label className="Form-Label" htmlFor="passwordCheck">Confirmar Contraseña:</label>
                            <input
                                className="Form-Input"
                                type="password"
                                id="passwordCheck"
                                {...register('passwordCheck')}
                            />
                            {errors.passwordCheck && <span>{errors.passwordCheck.message}</span>}
                        </div>
                    </div>
                    <input
                        className="Button Button_primary"
                        type="submit"
                        value={isSubmitting ? "Actualizando..." : "Actualizar"}
                        disabled={isSubmitting} />
                </form >
                {canResetMfa && (
                    <div style={{ marginTop: '24px', borderTop: '1px solid #dadada', paddingTop: '16px' }}>
                        <h3>Seguridad MFA</h3>
                        <p style={{ color: '#5a5a5a', marginBottom: '8px' }}>Si el usuario perdió acceso a su autenticador, puede resetear su MFA. Deberá reconfigurarlo en su próximo inicio de sesión.</p>
                        <button
                            type="button"
                            className="Button Button_secondary"
                            onClick={handleResetMfa}
                            disabled={isResettingMfa}
                        >
                            {isResettingMfa ? 'Reseteando...' : 'Resetear MFA del usuario'}
                        </button>
                    </div>
                )}
            </div >
        </div >
    );
}